import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useIsMutating } from '@tanstack/react-query';

/**
 * Variant styling for the `variant` API (see the note on the component below).
 *
 * Every one of these resolves to the brand ramp, so a primary action is the
 * same green whether it sits in Sales or in Legal. The `displayType` path
 * below is untouched and still renders exactly what it always did.
 */
const VARIANTS = {
  primary: 'border border-brand-700 bg-brand-700 text-white hover:bg-brand-600',
  secondary: 'border border-brand-700 bg-white text-brand-700 hover:bg-brand-50',
  danger: 'border border-danger bg-danger text-white hover:opacity-90',
  'danger-outline': 'border border-danger bg-white text-danger hover:bg-danger-bg',
  ghost: 'border border-transparent bg-transparent text-brand-700 hover:bg-brand-50'
};

/**
 * Radius rides with size, the way it does on the Sales and Management screens:
 * their small and icon buttons are `rounded-md`, their standard ones
 * `rounded-lg`. Same values, named for the job.
 */
const SIZES = {
  sm: 'px-3 py-1.5 text-xs rounded-action',
  md: 'px-4 py-2 text-sm rounded-control',
  lg: 'px-5 py-2.5 text-base rounded-control'
};

/**
 * The portal's button.
 *
 * Two APIs live here on purpose. `displayType` is the original — six string
 * values, each hand-written, some carrying literal hexes (#C2281B) and a
 * `hover:customBlue` class that was never a real Tailwind class and so never
 * did anything. 256 files call it and it is not worth a flag day, so it stays
 * exactly as it was.
 *
 * `variant` + `size` is what new work should use:
 *
 *   <Button variant="primary">Save</Button>
 *   <Button variant="secondary" size="sm">Cancel</Button>
 *   <Button variant="primary" fullWidth>Sign in</Button>
 *
 * It resolves to the brand ramp rather than to a one-off hex, takes a real
 * focus ring instead of `focus:outline-none` with nothing to replace it, and
 * puts the label directly in the button instead of wrapping it in a <p> — the
 * wrapper is why the old variants could not centre a label with an icon next
 * to it. Passing `variant` selects this path; omitting it changes nothing.
 */
const Button = ({
  children,
  onClick,
  displayType,
  type,
  disabled,
  customStyle,
  isLoading,
  variant,
  size = 'md',
  fullWidth = false,
  className = ''
}) => {
  const [internalLoading, setInternalLoading] = useState(false);
  const [autoLoading, setAutoLoading] = useState(false);
  const [wasClicked, setWasClicked] = useState(false);
  const isMutating = useIsMutating();
  const prevIsMutating = React.useRef(isMutating);

  useEffect(() => {
    // If mutations increased and this button was recently clicked, it likely triggered the mutation
    if (isMutating > prevIsMutating.current && wasClicked) {
      setAutoLoading(true);
      setWasClicked(false);
    }
    // If all mutations are done, stop loading
    if (isMutating === 0) {
      setAutoLoading(false);
    }
    prevIsMutating.current = isMutating;
  }, [isMutating, wasClicked]);

  const handleClick = async (e) => {
    // Mark as clicked so we can claim the next mutation start
    setWasClicked(true);
    // Timeout to reset wasClicked if no mutation starts (e.g. validaton error)
    setTimeout(() => setWasClicked(false), 1000);

    if (onClick) {
      const result = onClick(e);
      if (result instanceof Promise) {
        setInternalLoading(true);
        try {
          await result;
        } finally {
          setInternalLoading(false);
        }
      }
    }
  };

  // We detect loading state for submit and regular buttons by checking global mutation state.
  // This ensures buttons show loading even if the parent form resets 'disabled' state too early.
  // We include type === 'button' to cover actions like Delete/Duplicate that are not submit buttons but trigger mutations.
  const isBusy = isLoading || internalLoading || autoLoading;

  /**
   * New API, behind two guards rather than one.
   *
   * `variant` turned out not to be a free name: three call sites were already
   * passing it as a dead prop the old component ignored — two alongside
   * `displayType="secondary"`, one on its own. A bare `if (variant)` silently
   * re-rendered all three, which is exactly the kind of change that is
   * supposed to be impossible in an additive pass.
   *
   * So: the value must be one this component actually knows, and `displayType`
   * always wins when both are present. Anything unrecognised falls through to
   * the legacy branches untouched instead of being coerced to primary.
   */
  if (variant && VARIANTS[variant] && !displayType) {
    return (
      <button
        type={type}
        disabled={disabled || isBusy}
        style={customStyle}
        onClick={handleClick}
        className={`inline-flex items-center justify-center gap-2 font-medium transition-colors
          focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2
          disabled:cursor-not-allowed disabled:opacity-50
          ${VARIANTS[variant]} ${SIZES[size] ?? SIZES.md}
          ${fullWidth ? 'w-full' : ''} ${className}`}>
        {isBusy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : children}
      </button>
    );
  }

  if (displayType === 'downloadButton') {
    return (
      <button
        type={type}
        disabled={disabled || isBusy}
        className={`border ${displayType === 'secondary' ? 'border-customBlue' : 'border-customBlue bg-customBlue'} text-white  py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline flex justify-center items-center`}
        style={customStyle}
        onClick={handleClick}>
        {isBusy ? <Loader2 className="animate-spin h-5 w-5" /> : children}
      </button>
    );
  }
  if (displayType === 'primary-red') {
    return (
      <button
        type={type}
        className={`bg-[#C2281B] py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline flex justify-center items-center`}
        disabled={disabled || isBusy}
        onClick={handleClick}>
        <p className={`text-stone-100 hover:customBlue`}>
          {isBusy ? <Loader2 className="animate-spin h-5 w-5" /> : children}
        </p>
      </button>
    );
  }

  if (displayType === 'full-w-primary') {
    return (
      <button
        type={type}
        className={`border ${displayType === 'secondary' ? 'border-customBlue' : 'border-customBlue bg-customBlue'}  py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline w-full flex justify-center items-center`}
        disabled={disabled || isBusy}
        onClick={handleClick}>
        <p className={`text-stone-100 hover:customBlue`}>
          {isBusy ? <Loader2 className="animate-spin h-5 w-5" /> : children}
        </p>
      </button>
    );
  }

  if (displayType === 'full-w-secondary') {
    return (
      <button
        type={type}
        className={`border  border-customBlue text-customBlue py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline w-full flex justify-center items-center`}
        disabled={disabled || isBusy}
        onClick={handleClick}>
        <p className={`hover:customBlue`}>
          {isBusy ? <Loader2 className="animate-spin h-5 w-5" /> : children}
        </p>
      </button>
    );
  }

  if (displayType === 'secondary-red') {
    return (
      <button
        type={type}
        className={`border border-[#C2281B]  py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline flex justify-center items-center`}
        disabled={disabled || isBusy}
        onClick={handleClick}>
        <p className={`text-[#C2281B] hover:customBlue`}>
          {isBusy ? <Loader2 className="animate-spin h-5 w-5" /> : children}
        </p>
      </button>
    );
  }

  return (
    <button
      type={type}
      disabled={disabled || isBusy}
      className={`border ${displayType === 'secondary' ? 'border-customBlue' : 'border-customBlue bg-customBlue'}  py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline flex justify-center items-center`}
      style={customStyle}
      onClick={handleClick}>
      <p
        className={`${displayType === 'secondary' && '!text-customBlue'} text-white hover:customBlue`}>
        {isBusy ? <Loader2 className="animate-spin h-5 w-5" /> : children}
      </p>
    </button>
  );
};

export default Button;
